import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Stack, TextField, Typography} from 'views/components';
import {SearchOutlined as SearchOutlinedIcon} from 'views/icons';
import {translate} from 'views/module';

import backend from 'utils/backend';

const CaregiverTab = (props) => {
  const {
    onSelectAssignee,
  } = props;

  const [caregivers, setCaregivers] = React.useState([]);
  const [assigneeSearch, setAssigneeSearch] = React.useState('');
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  const rootRef = React.useRef(null);

  const assigneeSearchQuery = assigneeSearch.trim().toLowerCase();

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [assigneeSearchQuery]);

  React.useEffect(() => {
    rootRef.current?.focus({preventScroll: true});
  }, [rootRef]);

  React.useEffect(() => {
    let mounted = true;
    const timeoutId = setTimeout(async () => {
      setIsLoading(true);

      try {
        const filter = [];

        if (assigneeSearchQuery) {
          filter.push({key: 'caregiverName', value: `${assigneeSearchQuery}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.caregiver.queryCaregiver(filter, sortBy);

        if (mounted) {
          setCaregivers(result?.list || []);
        }
      } catch (error) {
        console.error('Failed to load caregivers in assignee picker', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }, 250);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [assigneeSearchQuery]);

  const handleSearchKeyDown = React.useCallback((event) => {
    const isHandledKey = [
      'ArrowDown',
      'ArrowUp',
      'Enter',
    ].includes(event.key);

    if (!isHandledKey) {
      return;
    }

    event.preventDefault();

    if (!caregivers.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      setHighlightedOptionIndex((previousIndex) => (previousIndex + 1) % caregivers.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      setHighlightedOptionIndex((previousIndex) => (previousIndex - 1 + caregivers.length) % caregivers.length);
      return;
    }

    if (event.key === 'Enter') {
      const highlightedAssignee = caregivers[highlightedOptionIndex] || caregivers[0];

      if (highlightedAssignee) {
        onSelectAssignee('caregivers', highlightedAssignee);
      }
    }
  }, [caregivers, highlightedOptionIndex, onSelectAssignee]);

  return (
    <Stack
      ref={rootRef}
      spacing={1.5}
      tabIndex={0}
      onKeyDown={handleSearchKeyDown}
    >
      <TextField
        fullWidth
        variant='outlined'
        value={assigneeSearch}
        onChange={setAssigneeSearch}
        placeholder={translate('tasks.Common.AssigneePicker.SearchPlaceholder')}
        icon={SearchOutlinedIcon}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />
      <Box
        sx={{
          overflowY: 'auto',
          maxHeight: 290,
          pr: 0.5,
        }}
      >
        <Stack spacing={0.25}>
          {
            isLoading &&
              <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                <CircularProgress size={16} />
              </Box>
          }
          {
            !isLoading && caregivers.map((caregiver, optionIndex) => (
              <Box
                key={`caregivers-${caregiver.caregiverRn}`}
                component='button'
                onMouseEnter={() => setHighlightedOptionIndex(optionIndex)}
                onClick={() => onSelectAssignee('caregivers', caregiver)}
                sx={{
                  background: 'none',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: optionIndex === highlightedOptionIndex ? '#F7F4EF' : 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  px: 0.75,
                  py: 0.75,
                  color: '#23201C',
                  fontSize: '16px',
                }}
              >
                {caregiver.caregiverName}
              </Box>
            ))
          }
          {
            !isLoading && caregivers.length === 0 &&
              <Typography variant='body2' color='text.secondary' sx={{px: 0.75}}>
                {translate('tasks.Common.AssigneePicker.NoCaregiversFound')}
              </Typography>
          }
        </Stack>
      </Box>
    </Stack>
  );
};

CaregiverTab.propTypes = {
  onSelectAssignee: PropTypes.func.isRequired,
};

export default React.memo(CaregiverTab);
